import React, { useEffect, useState } from 'react';

import classnames from 'classnames';
import PropTypes from 'prop-types';

import { ChildrenSchema } from 'packages/utils';

import { clearLocalStorageByOrg } from 'util/clearStorageByOrg';

import MenuOption from '../MenuOption';
import styles from './styles.module.less';

const OptionSchema = PropTypes.shape({
  label: ChildrenSchema.isRequired,
  value: PropTypes.string.isRequired,
});

const MenuSelector = React.memo(
  ({
    ariaLabel = '',
    className = '',
    footer = null,
    onChange = null,
    options = null,
    placeholder = '',
    value = '',
  }) => {
    const menuOptions = options || [];
    const selected = menuOptions.find(opt => opt.value === value);
    const [isOpen, setIsOpen] = useState(false);

    const onToggleOpen = () => setIsOpen(!isOpen);
    const onClose = () => setIsOpen(false);

    useEffect(() => {
      if (!isOpen) {
        return undefined;
      }
      const onKeyDown = event => {
        if (event.key === 'Escape') {
          onClose();
        }
      };
      document.addEventListener('keydown', onKeyDown);
      return () => document.removeEventListener('keydown', onKeyDown);
    }, [isOpen]);

    const onValueChange = event => {
      onClose();
      if (event.currentTarget.value !== value) {
        onChange?.(event);
        clearLocalStorageByOrg();
      }
    };

    return (
      <div className={classnames(styles.wrapper, className)}>
        <div className={styles.content}>
          <MenuOption
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-label={ariaLabel || undefined}
            as="button"
            className={classnames(styles.selector, isOpen && styles.selectorOpen)}
            onClick={onToggleOpen}
            type="button"
          >
            <span className={classnames(styles.optionContent, !selected && styles.empty)}>
              {selected ? selected.label : placeholder}
            </span>
            <i aria-hidden className="material-symbols-outlined">
              unfold_more
            </i>
          </MenuOption>
          <div className={classnames(styles.options, isOpen && styles.optionsOpen)}>
            <div className={styles.optionsList}>
              {menuOptions.map(opt => (
                <MenuOption
                  as="button"
                  className={styles.option}
                  key={opt.value}
                  onClick={onValueChange}
                  selected={opt.value === value}
                  type="button"
                  value={opt.value}
                >
                  <span className={styles.optionContent}>{opt.label}</span>
                </MenuOption>
              ))}
            </div>
            {footer}
          </div>
        </div>
        <div
          aria-hidden
          className={classnames(styles.layer, isOpen && styles.layerOpen)}
          onClick={onClose}
        />
      </div>
    );
  },
);

MenuSelector.propTypes = {
  options: PropTypes.arrayOf(OptionSchema).isRequired,

  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  footer: ChildrenSchema,
  onChange: PropTypes.func,
  placeholder: ChildrenSchema,
  value: PropTypes.string,
};

export default MenuSelector;
